import * as React from 'react';
// Import props.
import {
	ComparativeDataHeaderProps,
	ComparativeDataRowProps,
	ComparativePolicyInformationSummaryProps,
	IconButtonProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	BroadLineOfBusiness,
    PhoneContactType,
} from '@app/enumerations';
// Import services.
import {
	AddressService,
	DateService,
	StringService,
} from '@app/services';
// Import interfaces 
import {
	IAddress,
} from '@app/interfaces';
// Import mui modules.
import {
	DescriptionOutlined,
	GppGoodOutlined,
	PrintOutlined,
} from '@mui/icons-material';

interface UseComparativePolicyInformationSummaryParameters {
	props: ComparativePolicyInformationSummaryProps;
}

interface UseComparativePolicyInformationSummaryResults {
	applyPolicyComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyPolicyInformationComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyInsuredInformationComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyInsuredAddressComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyInsuredContactComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyPolicyInformationComparativeDataRowProps: () => ComparativeDataRowProps[];
	applyInsuredInformationComparativeDataRowProps: () => ComparativeDataRowProps[];
	policyInformationBaseId: string;
	insuredInformationBaseId: string;
}

const useComparativePolicyInformationSummary = (params: UseComparativePolicyInformationSummaryParameters): UseComparativePolicyInformationSummaryResults => {

	// #region exported methods

	/**
	 * Returns props for the landing header.
	 */
	const applyPolicyComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: generateIdForPolicyHeaderTitle(),
			isSticky: areHeadersSticky,
			title: `Policy ${params.props.policyName}`,
			titleIcon: GppGoodOutlined,
			titleFontSize: 'md',
			iconButtons: applyPolicyHeaderIconButtonPropsList(),
			spacingBetweenIconAndTitle: 1,
			paddingProps: {
				bottom: 1,
				top: 1,
			}
		};
	}

	/**
	 * Returns policy information comparative data header props. 
	 **/
	const applyPolicyInformationComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: generateIdForPolicyInformationHeaderTitle(),
			isSticky: false,
			title: 'Policy Information',
			titleFontSize: 'sm',
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	};

	/**
	 * Returns insured information comparative data header props. 
	 **/
	const applyInsuredInformationComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: generateIdForInsuredInformationHeaderTitle(),
			isSticky: false,
			title: 'Insured Information',
			titleFontSize: 'sm',
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	};

	/**
	 * Returns dwelling address comparative data header props. 
	 **/
	const applyInsuredAddressComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: generateIdForInsuredAddressHeaderTitle(),
			isSticky: false,
			title: singleLineInsuredAddress,
			titleColor: AppThemeColor.INFO,
			titleFontSize: 'xxsm',
			titleFontStyle: 'italic',
			titleFontWeight: 'normal',
			paddingProps: {
				bottom: 1,
				top: 1,
			},
		};
	};

	const applyInsuredContactComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: generateIdForInsuredInformationHeaderTitle(),
			isSticky: false,
			title: 'Contact Information',
			titleFontSize: 'xxsm',
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	}

	/**
	 * Returns policy information comparative data row props. 
	 **/
	const applyPolicyInformationComparativeDataRowProps = (): ComparativeDataRowProps[] => {
		let comparativeDataRowProps: ComparativeDataRowProps[] = [
			{
				fieldLabel: 'Policy Term',
				oldValue: currentTermDateRangeToDisplay,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
			{
				fieldLabel: 'Producer Organization',
				oldValue: producerOrganizationToDisplay,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
			{
				fieldLabel: 'Producer Organization Phone',
				oldValue: producerOrganizationPhoneToDisplay,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
			{
				fieldLabel: 'Producer',
				oldValue: producerToDisplay,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
			{
				fieldLabel: 'Covered Perils',
				oldValue: coveredPerilsToDisplay,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
		];

		if (params.props.broadLineOfBusiness === BroadLineOfBusiness.PERSONAL) {
			comparativeDataRowProps.push({				
				fieldLabel: 'Flood Zone',
				oldValue: floodZoneToDisplay,
				hideNewValueIfSame: true,	
				hideNewValueIfUndefinedOrEmpty: true,
			});
		}

		return comparativeDataRowProps;
	};

	/**
	 * Returns insured information comparative data row props. 
	 **/
	const applyInsuredInformationComparativeDataRowProps = (): ComparativeDataRowProps[] => {
		let comparativeDataRowProps: ComparativeDataRowProps[] = [
			{
				fieldLabel: isCommercial ? 'Company Name' : 'Name',
				oldValue: insuredFullNameToDisplay,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
			{
				fieldLabel: 'Home Phone',
				oldValue: insuredHomePhoneNumberToDisplay,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
			{
				fieldLabel: 'Mobile Phone',
				oldValue: insuredMobilePhoneNumberToDisplay,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
			{
				fieldLabel: 'Email',
				oldValue: insuredEmailToDisplay,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
		];

		return comparativeDataRowProps;
	};
	

	// #endregion exported methods


	// #region private methods

	/**
	 * Fetches a list of icon button props for the policy header props.
	 */
	const applyPolicyHeaderIconButtonPropsList = (): IconButtonProps[] => {
		let iconButtonProps: IconButtonProps[] = [];
		if (params.props.onPrintView !== undefined) {
			iconButtonProps.push({
				icon: PrintOutlined,
				label: 'Print',
				onClick: params.props.onPrintView,
			});
		}
		if (params.props.onViewPolicyPacket !== undefined) {
			iconButtonProps.push({
				icon: DescriptionOutlined,
				label: 'Policy Packet',
				onClick: params.props.onViewPolicyPacket,
			});
		}

		return iconButtonProps;
	}

	// #region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForSummary= (): string => {
		return params.props.id;
	}

	/**
	 * Generates id for the policy header title.
	 */
	const generateIdForPolicyHeaderTitle = (): string => {
		return `${generateIdForSummary()}-policyHeaderTitle`;
	}

	/**
	 * Generates id for the policy information header title.
	 */
	const generateIdForPolicyInformationHeaderTitle = (): string => {
		return `${generateIdForSummary()}-policyInformationHeaderTitle`;
	}

	/**
	 * Generates id for the insured information header title.
	 */
	const generateIdForInsuredInformationHeaderTitle = (): string => {
		return `${generateIdForSummary()}-insuredInformationHeaderTitle`;
	}

	/**
	 * Generates id for the insured address header title.
	 */
	const generateIdForInsuredAddressHeaderTitle = (): string => {
		return `${generateIdForSummary()}-insuredAddressHeaderTitle`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	const areHeadersSticky: boolean = React.useMemo(() => {
		return params.props.areHeadersSticky ?? false;
	}, [params.props.areHeadersSticky]);

	const policyInformationBaseId: string = React.useMemo(() => {
		return `${params.props.id}-policyInformation`;
	}, [params.props.id]);

	const insuredInformationBaseId: string = React.useMemo(() => {
		return `${params.props.id}-insuredInformation`;
	}, [params.props.id]);

	// Policy Information variables.
	const currentTermDateRangeEffectiveDateStr: string | undefined = React.useMemo(() => {
		let newCurrentTermDateRangeEffectiveDateStr: string | undefined = undefined;
		if (params.props.currentTermEffectiveDate !== undefined) {
			newCurrentTermDateRangeEffectiveDateStr = DateService.getInstance().formatDate(params.props.currentTermEffectiveDate);
		}
		return newCurrentTermDateRangeEffectiveDateStr;
	}, [params.props.currentTermEffectiveDate]);

	const currentTermDateRangeEndDateStr: string | undefined = React.useMemo(() => {
		let newCurrentTermDateRangeEndDateStr: string | undefined = undefined;
		if (params.props.currentTermEndDate !== undefined) {
			newCurrentTermDateRangeEndDateStr = DateService.getInstance().formatDate(params.props.currentTermEndDate);
		}
		return newCurrentTermDateRangeEndDateStr;
	}, [params.props.currentTermEndDate]);

	const currentTermDateRangeToDisplay: string | undefined = React.useMemo(() => {
		let newCurrentTermDateRangeStr: string | undefined = undefined;
		if (currentTermDateRangeEffectiveDateStr !== undefined
			&& currentTermDateRangeEndDateStr !== undefined) {
			newCurrentTermDateRangeStr = `${currentTermDateRangeEffectiveDateStr} - ${currentTermDateRangeEndDateStr}`;
		}
		return newCurrentTermDateRangeStr;
	}, [currentTermDateRangeEffectiveDateStr, currentTermDateRangeEndDateStr]);

	const producerOrganizationToDisplay: string | undefined = React.useMemo(() => params.props.producerInfo?.producerAgency?.name ?? undefined, [params.props.producerInfo?.producerAgency?.name]);

	const producerOrganizationPhoneToDisplay: string | undefined = React.useMemo(() => {
		let newProducerOrganizationPhone: string | undefined = undefined;
		if (params.props.producerInfo?.producerAgency?.phone !== undefined
			&& params.props.producerInfo?.producerAgency?.phone !== null) {
			newProducerOrganizationPhone = StringService.getInstance().formatPhoneNumber(params.props.producerInfo.producerAgency.phone);
		}
		return newProducerOrganizationPhone;
	}, [params.props.producerInfo?.producerAgency?.phone]);

	const producerToDisplay: string | undefined = React.useMemo(() => {
		return params.props.producerInfo?.producerAgent?.name ?? undefined;
	}, [params.props.producerInfo?.producerAgent?.name]);

	const coveredPerilsToDisplay: string | undefined = React.useMemo(() => {
		return params.props.coveredPerils.join(", ");
	}, [params.props.coveredPerils]);

	const floodZoneToDisplay: string | undefined = React.useMemo(() => {
		return params.props.floodZone;
	}, [params.props.floodZone]);

	// Insured information variables.

	const isCommercial: boolean = React.useMemo(() => {
		return params.props.broadLineOfBusiness === BroadLineOfBusiness.COMMERCIAL;
	}, [params.props.broadLineOfBusiness]);

	const insuredFirstName: string | undefined = React.useMemo(() => {
		return params.props.insuredInfo?.name?.firstNameOrCompanyName ?? undefined;
	}, [params.props.insuredInfo?.name?.firstNameOrCompanyName]);

	const insuredLastName: string | undefined = React.useMemo(() => {
		return params.props.insuredInfo?.name?.lastName ?? undefined;
	}, [params.props.insuredInfo?.name?.lastName]);

	const insuredFullNameToDisplay: string | undefined = React.useMemo(() => {
		return insuredFirstName !== undefined || insuredLastName !== undefined
			? [insuredFirstName, insuredLastName]
				.filter((token) => !String.isNullOrWhiteSpace(token))
				.join(" ")
			: undefined;
	}, [insuredFirstName, insuredLastName]);

	const insuredHomePhoneNumber: string | undefined = React.useMemo(() => {
		return params.props.insuredInfo?.phoneNumbers.find((phoneNumber) => phoneNumber.contactType === PhoneContactType.HOME)?.number ?? undefined;
	}, [params.props.insuredInfo?.phoneNumbers]);

	const insuredHomePhoneNumberToDisplay: string | undefined = React.useMemo(() => {
		return insuredHomePhoneNumber !== undefined ? StringService.getInstance().formatPhoneNumber(insuredHomePhoneNumber) : undefined;
	}, [insuredHomePhoneNumber]);

	const insuredMobilePhoneNumber: string | undefined = React.useMemo(() => {
		return params.props.insuredInfo?.phoneNumbers.find((phoneNumber) => phoneNumber.contactType === PhoneContactType.MOBILE)?.number ?? undefined;
	}, [params.props.insuredInfo?.phoneNumbers]);

	const insuredMobilePhoneNumberToDisplay: string | undefined = React.useMemo(() => {
		return insuredMobilePhoneNumber !== undefined ? StringService.getInstance().formatPhoneNumber(insuredMobilePhoneNumber) : undefined;
	}, [insuredMobilePhoneNumber]);

	const insuredEmailToDisplay: string | undefined = React.useMemo(() => {
		return params.props.insuredInfo?.email ?? undefined;
	}, [params.props.insuredInfo?.email]);

	const addressRecord: IAddress = React.useMemo(() => {
		return {
			addressLine1: params.props.insuredInfo?.mailingAddress?.addressLine1 ?? String.empty(),
			addressLine2: params.props.insuredInfo?.mailingAddress?.addressLine2 ?? String.empty(),
			city: params.props.insuredInfo?.mailingAddress?.city ?? String.empty(),
			state: params.props.insuredInfo?.mailingAddress?.state ?? String.empty(),
			postalCode: params.props.insuredInfo?.mailingAddress?.postalCode ?? String.empty(),
		};
	}, [params.props.insuredInfo?.mailingAddress]);
	const singleLineInsuredAddress: string = React.useMemo(() => AddressService.getInstance().formatAddressToSingleLineString(addressRecord), [addressRecord]);

	// #endregion private variables

	// #region lifecycle methods.

	// #endregion lifecycle methods.

	return {
		applyPolicyComparativeDataHeaderProps,
		applyPolicyInformationComparativeDataHeaderProps,
		applyInsuredInformationComparativeDataHeaderProps,
		applyInsuredAddressComparativeDataHeaderProps,
		applyInsuredContactComparativeDataHeaderProps,
		applyPolicyInformationComparativeDataRowProps,
		applyInsuredInformationComparativeDataRowProps,
		policyInformationBaseId,
		insuredInformationBaseId,
	};
}

// Export parameters.
export {
	UseComparativePolicyInformationSummaryParameters,
}

// Export results.
export {
	UseComparativePolicyInformationSummaryResults,
}

export default useComparativePolicyInformationSummary;